/**
 * Linha do tempo da conversa (Versão B do protótipo aprovado): uma linha por mensagem, com foto e
 * nome do AUTOR — essencial em grupo —, o conteúdo por tipo (texto, foto, vídeo, áudio com player,
 * figurinha, documento, localização), citação, reações e estado de entrega.
 *
 * Nome do autor: nome salvo → nome público → telefone formatado (regra única em
 * `packages/integration-wapi/src/events.ts`); a tela nunca mostra JID (`stripJid` é a rede de segurança).
 * Reagir manda `onReact`; clicar de novo na própria reação a remove (emoji vazio).
 */
import React, { useState, type ReactElement } from "react";
import { FileText, MapPin, Reply, Smile } from "lucide-react";
import type { ChatMessage } from "../../api/ai-types.js";
import { Badge } from "../../ui/index.js";
import { AudioPlayer } from "./AudioPlayer.js";
import { ChatAvatar } from "./ChatAvatar.js";
import { QUICK_REACTIONS, authorColor, dayLabel, messageTime, senderLabel, statusGlyph, statusLabel, stripJid } from "./helpers.js";

export type TimelineMessage = Omit<ChatMessage, "text" | "sender"> & { text: string; sender: ChatMessage["sender"] };

function Body({ message, fallback, onTranscribe }: { message: TimelineMessage; fallback: string; onTranscribe?: (messageId: string) => Promise<string> }): ReactElement {
  const media = message.media;
  const caption = message.text ? <p className="m-0 whitespace-pre-wrap break-words text-base text-ink">{message.text}</p> : null;
  switch (message.messageType) {
    case "image":
      return (
        <div className="flex flex-col gap-1.5">
          {media?.url ? (
            <a href={media.url} target="_blank" rel="noreferrer" aria-label="Abrir foto em tamanho real">
              <img src={media.url} alt={message.text || "Foto recebida"} loading="lazy" className="max-h-72 max-w-full rounded-control border border-border object-contain" />
            </a>
          ) : <p className="m-0 text-sm italic text-muted" data-media="unavailable">Foto indisponível (arquivo do histórico não foi importado).</p>}
          {caption}
        </div>
      );
    case "video":
      return (
        <div className="flex flex-col gap-1.5">
          {media?.url ? <video src={media.url} controls preload="metadata" className="max-h-72 max-w-full rounded-control border border-border" /> : <p className="m-0 text-sm italic text-muted" data-media="unavailable">Vídeo indisponível (arquivo do histórico não foi importado).</p>}
          {caption}
        </div>
      );
    case "audio":
      // Só áudio recebido é transcrito (o que a loja grava, a equipe já sabe o que disse).
      return <AudioPlayer media={media} label={fallback} onTranscribe={message.sender === "customer" && onTranscribe ? () => onTranscribe(message.id) : undefined} />;
    case "sticker":
      return media?.url
        ? <img src={media.url} alt="Figurinha" loading="lazy" className="h-32 w-32 object-contain" data-sticker="true" />
        : <p className="m-0 text-sm italic text-muted" data-media="unavailable">Figurinha</p>;
    case "document":
      return (
        <div className="flex flex-col gap-1.5">
          {media?.url ? (
            <a href={media.url} target="_blank" rel="noreferrer" className="inline-flex items-center gap-2 rounded-control border border-border bg-surface px-3 py-2 text-sm font-bold text-ink hover:bg-surface-soft">
              <FileText className="h-4 w-4 shrink-0" aria-hidden focusable={false} />
              <span className="truncate">{media.fileName ?? "Documento"}</span>
            </a>
          ) : <p className="m-0 text-sm italic text-muted" data-media="unavailable">{message.media?.fileName ?? "Documento"} (arquivo do histórico não foi importado).</p>}
          {caption}
        </div>
      );
    case "location":
      return message.location ? (
        <a href={`https://www.google.com/maps?q=${message.location.latitude},${message.location.longitude}`} target="_blank" rel="noreferrer" className="inline-flex items-center gap-2 text-sm font-bold text-info underline">
          <MapPin className="h-4 w-4" aria-hidden focusable={false} />
          {message.location.name ?? "Ver localização no mapa"}
        </a>
      ) : <p className="m-0 text-sm italic text-muted">Localização</p>;
    case "unknown":
      return <p className="m-0 text-sm italic text-muted">Mensagem não suportada por esta tela.</p>;
    default:
      return caption ?? <p className="m-0 text-sm italic text-muted">Mensagem vazia.</p>;
  }
}

/** Trecho que a caixa de resposta mostra ao citar: texto, ou o tipo da mídia quando não há texto. */
function replyPreview(message: TimelineMessage): string {
  if (message.text) return message.text.slice(0, 160);
  const labels: Record<string, string> = { image: "Foto", video: "Vídeo", audio: "Áudio", document: "Documento", sticker: "Figurinha", location: "Localização" };
  return labels[message.messageType ?? ""] ?? "Mensagem";
}

function ReactionBar({ message, onReact }: { message: TimelineMessage; onReact?: (messageId: string, emoji: string) => void }): ReactElement | null {
  if (!message.reactions?.length) return null;
  return (
    <div className="mt-1 flex flex-wrap gap-1" aria-label="Reações">
      {message.reactions.map((reaction) => (
        <button
          key={reaction.emoji}
          type="button"
          disabled={!onReact}
          onClick={() => onReact?.(message.id, reaction.mine ? "" : reaction.emoji)}
          title={reaction.by.join(", ")}
          aria-pressed={reaction.mine}
          className={"rounded-pill border px-2 text-sm font-bold leading-6 " + (reaction.mine ? "border-brand bg-brand-wash text-ink" : "border-border bg-surface text-ink")}
        >
          {reaction.emoji} {reaction.count}
        </button>
      ))}
    </div>
  );
}

export function ChatTimeline({ messages, conversationName, onReact, onTranscribe, onReply, now }: { messages: TimelineMessage[]; conversationName: string; onReact?: (messageId: string, emoji: string) => void; onTranscribe?: (messageId: string) => Promise<string>; onReply?: (target: { messageId: string; authorName: string; text: string }) => void; now?: Date }): ReactElement {
  const [picking, setPicking] = useState<string | null>(null);
  const reference = now ?? new Date();
  let lastDay = "";
  return (
    <ol className="conversation-timeline m-0 flex min-h-0 flex-1 list-none flex-col gap-0.5 overflow-y-auto bg-surface p-5" aria-label="Mensagens da conversa">
      {messages.length === 0 ? <li className="m-auto text-sm text-muted">Nenhuma mensagem trocada ainda.</li> : null}
      {messages.map((message) => {
        const day = dayLabel(message.createdAt, reference);
        const separator = day && day !== lastDay ? day : null;
        lastDay = day || lastDay;
        const outgoing = message.sender !== "customer";
        const name = stripJid(senderLabel(message, conversationName));
        const color = outgoing ? undefined : authorColor(message.author?.phone ?? name);
        const label = statusLabel(message.status);
        return (
          <React.Fragment key={message.id}>
            {separator ? <li className="my-3 self-center rounded-pill border border-border bg-surface-soft px-3 py-0.5 text-xs font-bold text-muted" aria-label={`Mensagens de ${separator}`}>{separator}</li> : null}
            <li data-record-id={message.id} data-sender={message.sender} data-message-type={message.messageType ?? "text"} className="group relative grid grid-cols-[2.25rem_minmax(0,1fr)] gap-3 rounded-control px-2 py-1.5 hover:bg-surface-soft">
              {outgoing ? (
                <span className={"grid h-9 w-9 place-items-center rounded-pill text-xs font-extrabold " + (message.sender === "ai" ? "bg-info-soft text-info" : "bg-success-soft text-success")} aria-hidden>{message.sender === "ai" ? "IA" : "VC"}</span>
              ) : (
                <ChatAvatar name={name} pictureUrl={message.author?.pictureUrl} />
              )}
              <div className="min-w-0">
                <div className="flex flex-wrap items-baseline gap-x-2 gap-y-0.5">
                  <span className={"text-sm font-black " + (outgoing ? (message.sender === "ai" ? "text-info" : "text-success") : "")} style={color ? { color } : undefined}>{name}</span>
                  {message.sender === "customer" && message.author && !message.author.hasName ? <Badge tone="neutral">sem nome público</Badge> : null}
                  {message.sender === "ai" ? <Badge tone="info">IA</Badge> : null}
                  <time dateTime={message.createdAt} className="text-xs text-muted">{messageTime(message.createdAt, reference)}</time>
                  {outgoing && label ? <span className={"text-xs " + (message.status === "read" || message.status === "played" ? "font-bold text-info" : "text-muted")} title={label} aria-label={label}>{statusGlyph(message.status)}</span> : null}
                  {message.dispatch === "queued" ? <Badge tone="warning">Na fila (fora do horário)</Badge> : null}
                </div>
                {message.quoted ? (
                  <blockquote className="m-0 my-1 border-l-[3px] border-brand bg-surface-soft px-2 py-1 text-xs">
                    <b className="block text-brand-text">{message.quoted.authorName ? stripJid(message.quoted.authorName) : "Mensagem citada"}</b>
                    <span className="text-muted">{message.quoted.text ?? "Mensagem"}</span>
                  </blockquote>
                ) : null}
                <div className="mt-0.5"><Body message={message} fallback={name} onTranscribe={onTranscribe} /></div>
                <ReactionBar message={message} onReact={onReact} />
              </div>
              {(onReact || onReply) && message.externalMessageId ? (
                // Flutua acima da borda da linha (-top-3), fora da área do conteúdo: antes ficava em
                // cima do botão de velocidade do player de áudio, que ocupa a largura toda.
                <div className="absolute -top-3 right-2 z-10 hidden items-center gap-1 group-hover:flex group-focus-within:flex">
                  {onReact && picking === message.id ? (
                    <span role="menu" aria-label="Reagir" className="flex gap-0.5 rounded-pill border border-border bg-surface px-1.5 py-0.5 shadow-s1">
                      {QUICK_REACTIONS.map((emoji) => (
                        <button key={emoji} type="button" role="menuitem" aria-label={`Reagir com ${emoji}`} onClick={() => { onReact(message.id, emoji); setPicking(null); }} className="h-8 w-8 rounded-pill text-lg hover:bg-surface-soft">{emoji}</button>
                      ))}
                    </span>
                  ) : null}
                  {onReply ? (
                    <button type="button" aria-label={`Responder a ${name}`} data-action="reply" onClick={() => onReply({ messageId: message.id, authorName: name, text: replyPreview(message) })} className="grid h-7 w-7 place-items-center rounded-pill border border-border bg-surface text-muted shadow-s1 hover:text-ink">
                      <Reply className="h-4 w-4" aria-hidden focusable={false} />
                    </button>
                  ) : null}
                  {onReact ? <button type="button" aria-label="Reagir à mensagem" aria-expanded={picking === message.id} onClick={() => setPicking(picking === message.id ? null : message.id)} className="grid h-7 w-7 place-items-center rounded-pill border border-border bg-surface text-muted shadow-s1 hover:text-ink">
                    <Smile className="h-4 w-4" aria-hidden focusable={false} />
                  </button> : null}
                </div>
              ) : null}
            </li>
          </React.Fragment>
        );
      })}
    </ol>
  );
}
